import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '@/App';
import { toast } from 'sonner';
import { StickyNote, Save, X, Pencil } from 'lucide-react';

/**
 * Cancel-reason note popover that appears next to a cancelled DB-A row on
 * mouse hover. Click pencil to edit, Save to persist, Close to dismiss. The
 * note is parcel-level (stored on every sibling row of the same track_no via
 * the existing PATCH /database-a/record endpoint with field=cancel_note).
 */
const CancelNotePopover = ({ rec, anchorEl, onClose, onSaved, onMouseEnter, onMouseLeave }) => {
  const [editing, setEditing] = useState(!rec.cancel_note);
  const [text, setText] = useState(rec.cancel_note || '');
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  // Position popover to the right of the anchor row (fallback: above)
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const POP_W = 320;
    const POP_H = 220;
    let top = r.top + window.scrollY;
    let left = r.right + window.scrollX + 8;
    // If overflowing right edge, try anchoring to the left of the row
    if (left + POP_W > window.innerWidth - 16) {
      left = Math.max(16, r.left + window.scrollX - POP_W - 8);
    }
    // Clamp vertical position so the popover stays in viewport
    const maxTop = window.scrollY + window.innerHeight - POP_H - 16;
    if (top > maxTop) top = Math.max(window.scrollY + 16, maxTop);
    setPos({ top, left });
  }, [anchorEl]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target) && anchorEl && !anchorEl.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorEl, onClose]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/database-a/record`,
        { track_no: rec.track_no, product: rec.product || '', field: 'cancel_note', value: text },
        { headers: getAuthHeader() }
      );
      toast.success('Cancellation reason saved', { duration: 1500 });
      onSaved(text);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [rec, text, onSaved]);

  return (
    <div
      ref={ref}
      className="fixed z-50 w-[320px] bg-white rounded-xl shadow-2xl border border-pink-300 overflow-hidden animate-fade-in"
      style={{ top: pos.top, left: pos.left }}
      data-testid="cancel-note-popover"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-pink-50 border-b border-pink-200">
        <div className="flex items-center gap-1.5 text-pink-900 text-xs font-bold uppercase tracking-wide">
          <StickyNote className="w-3.5 h-3.5" />
          Cancellation Reason
        </div>
        <div className="flex items-center gap-1">
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="p-1 rounded hover:bg-pink-100 text-pink-700"
              title="Edit"
              data-testid="cancel-note-edit-btn"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-pink-100 text-pink-700"
            title="Close"
            data-testid="cancel-note-close-btn"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="px-3 py-2 text-[11px] text-slate-500 border-b border-slate-100">
        Track: <span className="font-mono text-slate-700">{rec.track_no}</span>
      </div>

      <div className="p-3">
        {editing ? (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); save(); }
                else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
              }}
              placeholder="e.g. Customer cancelled / Partner cancelled / Stock out / Address invalid…"
              rows={4}
              autoFocus
              className="w-full text-sm border border-slate-300 rounded-lg p-2 outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-200 resize-none"
              data-testid="cancel-note-textarea"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] text-slate-400">Ctrl+Enter to save • Esc to close</span>
              <button
                onClick={save}
                disabled={saving}
                className="px-2.5 py-1 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-xs font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
                data-testid="cancel-note-save-btn"
              >
                <Save className="w-3 h-3" />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        ) : (
          <div
            className="text-sm text-slate-700 whitespace-pre-wrap min-h-[60px] cursor-text"
            onDoubleClick={() => setEditing(true)}
            data-testid="cancel-note-display"
          >
            {text || <span className="text-slate-400 italic">No reason recorded — click ✎ to add one.</span>}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fade-in { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fade-in 0.12s ease-out; }
      `}</style>
    </div>
  );
};

export default CancelNotePopover;
